import React from 'react';
import PropTypes from 'prop-types';
import { findLastIndex, map } from 'lodash';
import { List, arrayMove } from 'react-movable';
import {
  Root,
  Item,
  Trigger,
  Content,
  Label,
  Portal,
  Sub,
  SubTrigger,
  SubContent,
  Separator,
} from '@radix-ui/react-dropdown-menu';

import { isValueEqual, classNames } from 'utils';

import Icon from 'components/icon';
import AppIcon from 'components/apps/icon';
import Tooltip from 'components/tooltip';

import './preferences-menu.scss';

/** @typedef {import('react-movable').OnChangeMeta} OnChangeMeta */

export default class ViewPreferencesMenu extends React.PureComponent {
  static contextTypes = {
    client: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    isOwner: PropTypes.bool,
    updateWithViewPreferences: PropTypes.func.isRequired,
    resetWithViewPreferences: PropTypes.func.isRequired,
  };

  static propTypes = {
    buttonChildren: PropTypes.any.isRequired,
    buttonClassName: PropTypes.string,
    buttonAriaLabel: PropTypes.string,
    options: PropTypes.array,
    items: PropTypes.array,
    clientItems: PropTypes.array,
    swellItems: PropTypes.array,
    preferenceKey: PropTypes.string,
    listOnly: PropTypes.bool,
    withPreferences: PropTypes.bool,
    side: PropTypes.string,
    sideOffset: PropTypes.number,
    align: PropTypes.string,
    alignOffset: PropTypes.number,
    cleanSaveItem: PropTypes.func,
    filter: PropTypes.func,
    avoidCollisions: PropTypes.bool,
    onClickItem: PropTypes.func,
    label: PropTypes.string,
    readyOptions: PropTypes.array,
    readyList: PropTypes.array,
    contentClassName: PropTypes.string,
    updateWithViewPreferences: PropTypes.func,
    resetWithViewPreferences: PropTypes.func,
  };

  constructor(props, context) {
    super(props, context);

    const state = this.getItemState();
    state.options = this.getOptions(state);
    state.open = false;

    this.state = state;

    /** @type {React.RefObject<List>} */
    this.listRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    // The tabs' "more" menu keeps the same items and passes a new filter
    // when the tabs that don't fit change
    if (
      prevProps.items !== this.props.items ||
      prevProps.filter !== this.props.filter
    ) {
      this.setState((state) => {
        const newState = {
          ...state,
          ...this.getItemState(),
        };

        newState.options = this.getOptions(newState);

        return newState;
      });
    }
  }

  getItemState() {
    const { items, filter } = this.props;

    if (!items) {
      return {
        visibleItems: [],
        hiddenItems: [],
        allItems: [],
      };
    }

    const origItems = this.getOriginalItems();
    const isClientDefault = this.getIsClientDefault(origItems);
    const isSwellDefault = this.getIsSwellDefault(origItems);
    const isClientUsingSwellDefault = this.getIsClientUsingSwellDefault();

    const visibleItems = items.filter((item) => !item.hidden);
    const hiddenItems = items.filter((item) => item.hidden);

    return {
      visibleItems: filter ? visibleItems.filter(filter) : visibleItems,
      hiddenItems: filter ? hiddenItems.filter(filter) : hiddenItems,
      allItems: origItems,
      origItems: this.state?.origItems || origItems,
      isClientDefault,
      isSwellDefault,
      isClientUsingSwellDefault,
    };
  }

  getOriginalItems() {
    const { items, cleanSaveItem } = this.props;

    if (!items) return [];

    return cleanSaveItem ? items.map(cleanSaveItem) : items;
  }

  getAllItemsSorted() {
    const { allItems } = this.state;
    const allItemsSorted = [...allItems];

    // Sort items with hidden last
    allItemsSorted.sort((a, b) => {
      if (!a.hidden && b.hidden) {
        return -1;
      } else if (a.hidden && !b.hidden) {
        return 1;
      } else {
        return 0;
      }
    });
    return allItemsSorted;
  }

  getIsClientDefault(origItems) {
    const { listOnly, clientItems, cleanSaveItem } = this.props;

    if (listOnly) return false;

    const cleanDefaults = cleanSaveItem
      ? clientItems.map(cleanSaveItem)
      : clientItems;

    return isValueEqual(cleanDefaults, origItems);
  }

  getIsSwellDefault(origItems) {
    const { listOnly, swellItems, cleanSaveItem } = this.props;

    if (listOnly) return false;

    const cleanDefaults = cleanSaveItem
      ? swellItems.map(cleanSaveItem)
      : swellItems;

    return isValueEqual(cleanDefaults, origItems);
  }

  getIsClientUsingSwellDefault() {
    const { listOnly, swellItems, clientItems, cleanSaveItem } = this.props;

    if (listOnly) return false;

    const cleanClientDefaults = cleanSaveItem
      ? clientItems.map(cleanSaveItem)
      : clientItems;

    const cleanSwellDefaults = cleanSaveItem
      ? swellItems.map(cleanSaveItem)
      : swellItems;

    return isValueEqual(cleanClientDefaults, cleanSwellDefaults);
  }

  getOptions(state) {
    if (this.props.listOnly) return [];

    const { options: optionProp } = this.props;
    const { isOwner } = this.context;
    const { isClientDefault, isSwellDefault, isClientUsingSwellDefault } =
      state;
    const canResetUser = !isClientDefault;
    const canSaveClient = isOwner && !isClientDefault;
    const canResetClient =
      isOwner && !isSwellDefault && !isClientUsingSwellDefault;

    const allOptions = [];

    if (optionProp) {
      allOptions.push(
        <Separator key="separator-1" className="preferences-menu-separator" />,
      );
    }

    if (canResetUser) {
      allOptions.push({
        label: 'Reset to store default',
        onClick: this.onClickResetToStoreDefaults,
      });
    }

    if (canResetUser && (canSaveClient || canResetClient)) {
      allOptions.push(
        <Separator key="separator-2" className="preferences-menu-separator" />,
      );
    }

    if (canSaveClient) {
      allOptions.push({
        label: 'Save as store default',
        onClick: this.onClickSaveDefaults,
      });
    }

    if (canResetClient) {
      allOptions.push({
        label: 'Reset store to Swell default',
        onClick: this.onClickResetToSwellDefaults,
      });
    }

    return allOptions;
  }

  onClickResetToStoreDefaults = (event) => {
    event.preventDefault();
    this.resetPreferences();
    this.setState({ open: false });
  };

  onClickResetToSwellDefaults = (event) => {
    event.preventDefault();
    this.resetPreferences(true);
    this.setState({ open: false });
  };

  onClickSaveDefaults = (event) => {
    event.preventDefault();
    this.updatePreferences(true);
    this.setState({ open: false });
  };

  updatePreferences(clientDefaults = false) {
    const { preferenceKey } = this.props;
    const updateWithViewPreferences =
      this.props.updateWithViewPreferences ||
      this.context.updateWithViewPreferences;
    return updateWithViewPreferences(
      { [preferenceKey]: this.state.allItems },
      clientDefaults,
    );
  }

  resetPreferences(clientDefaults = false) {
    const { preferenceKey } = this.props;
    const resetWithViewPreferences =
      this.props.resetWithViewPreferences ||
      this.context.resetWithViewPreferences;
    return resetWithViewPreferences({ [preferenceKey]: null }, clientDefaults);
  }

  onClickSaveDefault = (event) => {
    event.preventDefault();
  };

  onClickItem = (item, event) => {
    event.preventDefault();

    // This random variable seems to determine whether it was dragged or not
    if (this.listRef.current?.afterIndex === -2) {
      if (item) {
        this.props.onClickItem?.(item);
      }

      if (this.props.listOnly) {
        this.setState({ open: false });
      }
    }
  };

  /**
   * Event handler for hiding a config.
   * @param {Event} event - The event object.
   */
  onClickHideItem = (event, index) => {
    event.preventDefault();

    this.setState(
      (state) => {
        const targetId = state.visibleItems[index].id;
        const newItems = this.getAllItemsSorted();
        const oldIndex = newItems.findIndex((item) => item.id === targetId);
        const firstIndex = newItems.findIndex((item) => item.hidden);
        newItems[oldIndex].hidden = true;

        return {
          ...state,
          visibleItems: state.visibleItems.filter((_item, i) => i !== index),
          hiddenItems: [state.visibleItems[index], ...state.hiddenItems],
          allItems: arrayMove(
            newItems,
            oldIndex,
            firstIndex >= 0 ? firstIndex - 1 : 0,
          ),
        };
      },
      () => {
        this.updatePreferences();
      },
    );
  };

  /**
   * Event handler for showing a config.
   * @param {Event} event - The event object.
   */
  onClickShowItem = (event, index) => {
    event.preventDefault();

    const { hiddenItems } = this.state;

    if (hiddenItems[index].hiddenByConditions) {
      return;
    }

    this.setState(
      (state) => {
        const newItems = this.getAllItemsSorted();
        const itemId = state.hiddenItems[index].id;
        const oldIndex = newItems.findIndex((item) => item.id === itemId);
        const lastIndex = findLastIndex(newItems, (item) => !item.hidden);
        delete newItems[oldIndex].hidden;

        return {
          visibleItems: [...state.visibleItems, state.hiddenItems[index]],
          hiddenItems: state.hiddenItems.filter((_item, i) => i !== index),
          allItems: arrayMove(
            newItems,
            oldIndex,
            lastIndex >= 0 ? lastIndex + 1 : 0,
          ),
        };
      },
      () => {
        this.updatePreferences();
      },
    );
  };

  /** @param {OnChangeMeta} meta */
  onMoveChange = ({ oldIndex, newIndex }) => {
    this.setState(
      (state) => {
        const sourceId = state.visibleItems[oldIndex]?.id;
        const targetId = state.visibleItems[newIndex]?.id;

        const oldIndexInitial = state.allItems.findIndex(
          (item) => item.id === sourceId,
        );

        const newIndexInitial = state.allItems.findIndex(
          (item) => item.id === targetId,
        );

        const newItems = arrayMove(
          state.allItems,
          oldIndexInitial,
          newIndexInitial,
        );

        return {
          ...state,
          visibleItems: arrayMove(state.visibleItems, oldIndex, newIndex),
          allItems: newItems,
        };
      },
      () => {
        this.updatePreferences();
      },
    );
  };

  /**
   * Event handler for toggling a state.
   */
  onClickToggleState = () => {
    this.setState((state, props) => ({
      open: !state.open,
      // Set orig items to current items when opening
      origItems: state.open ? state.origItems : this.getOriginalItems(props),
    }));
  };

  renderList() {
    const { readyList } = this.props;

    return (
      <List
        ref={this.listRef}
        lockVertically={true}
        transitionDuration={200}
        values={readyList}
        renderList={({ children, props }) => <div {...props}>{children}</div>}
        renderItem={({ value, props }) => <div {...props}>{value}</div>}
      />
    );
  }

  renderVisibleItems() {
    const { listOnly } = this.props;
    const { visibleItems } = this.state;

    return (
      <List
        ref={this.listRef}
        lockVertically={true}
        transitionDuration={200}
        values={visibleItems.map((item) => ({
          ...item,
          disabled: listOnly,
        }))}
        onChange={this.onMoveChange}
        renderList={({ children, props }) => <div {...props}>{children}</div>}
        renderItem={({ value, props }) => (
          <div className="preferences-menu-movable" {...props}>
            {this.renderVisibleItem(value, props.key)}
          </div>
        )}
      />
    );
  }

  renderVisibleItem(item, index) {
    const { listOnly } = this.props;

    return (
      <div key={index} className="preferences-menu-item-container">
        <Item
          className="preferences-menu-item"
          onClick={this.onClickItem.bind(this, item)}
        >
          {!listOnly && (
            <div
              aria-label="Click and drag to reorder"
              className="preferences-menu-movable-trigger"
            >
              <Icon svgType="dots-grip" width={16} height={16} />
            </div>
          )}
          {this.renderAppIcon(item)}
          {item.label}
        </Item>
        {!listOnly && !item.default && (
          <button
            type="button"
            className="preferences-menu-hide-trigger"
            onClick={(event) => this.onClickHideItem(event, index)}
          >
            <Icon svgType="eye" width={16} height={16} />
          </button>
        )}
        {/* TODO: Start of visible options submenu for advanced features like `rename` shelved until next iteration */}
        {/* <Sub>
          <SubTrigger className="preferences-menu-submenu-trigger">
            <Icon svgType="dots-horizontal" />
          </SubTrigger>
          <Portal>
            <SubContent
              sideOffset={2}
              alignOffset={-6}
              className="preferences-menu-submenu-content"
            >
              {map(item.options, (option, index) =>
                this.renderOptionItems(option, index),
              )}
            </SubContent>
          </Portal>
        </Sub> */}
      </div>
    );
  }

  renderHiddenItems() {
    const { preferenceKey } = this.props;
    const { hiddenItems, visibleItems } = this.state;
    return (
      <>
        {visibleItems.length > 0 && (
          <Separator className="preferences-menu-separator" />
        )}
        <Label className="preferences-menu-label">Hidden {preferenceKey}</Label>
        {map(hiddenItems, (item, index) => this.renderHiddenItem(item, index))}
      </>
    );
  }

  renderHiddenItem(item, index) {
    return (
      <div
        key={index}
        className={classNames('preferences-menu-item-container', {
          default: item.hiddenByConditions,
        })}
      >
        <Item
          className="preferences-menu-item muted"
          onClick={this.onClickItem.bind(this, item)}
        >
          {this.renderAppIcon(item)}
          {item.label}
        </Item>
        <button
          type="button"
          className="preferences-menu-hide-trigger"
          onClick={(event) => this.onClickShowItem(event, index)}
        >
          {item.hiddenByConditions ? (
            <Tooltip message="Disabled" dir="left">
              <Icon svgType="eye-off" width={16} height={16} />
            </Tooltip>
          ) : (
            <Icon svgType="eye-off" width={16} height={16} />
          )}
        </button>
        {item.items?.length > 0 && (
          <Sub>
            <SubTrigger className="preferences-menu-submenu-trigger">
              <Icon svgType="dots-horizontal" />
            </SubTrigger>
            <Portal>
              <SubContent
                sideOffset={2}
                alignOffset={-6}
                className="preferences-menu-submenu-content"
              >
                {map(item.options, (option, index) =>
                  this.renderOptionItems(option, index),
                )}
              </SubContent>
            </Portal>
          </Sub>
        )}
      </div>
    );
  }

  renderAppIcon(item) {
    const {
      client: { appsById },
    } = this.context;

    const app = appsById?.[item.app_id];
    if (!app) {
      return null;
    }

    return <AppIcon image={app.logo_icon} name={app.name} size={16} />;
  }

  renderOptions() {
    const { readyOptions } = this.props;
    if (readyOptions && readyOptions.length === 0) {
      return null;
    }

    const options = readyOptions || this.state.options || [];
    return (
      <Sub>
        <Separator className="preferences-menu-separator" />
        <SubTrigger className="preferences-menu-submenu-options-trigger">
          Options
          <Icon svgType="chevron-right" />
        </SubTrigger>
        <Portal>
          <SubContent
            className="preferences-menu-submenu-content"
            align="middle"
            sideOffset={2}
            alignOffset={-5}
          >
            {map(options, (option, index) =>
              this.renderOptionItems(option, index),
            )}
          </SubContent>
        </Portal>
      </Sub>
    );
  }

  renderOptionItems(option, index) {
    return React.isValidElement(option) ? (
      option
    ) : !option ? null : (
      <Item
        key={index}
        onClick={option.onClick}
        className="preferences-menu-submenu-item"
      >
        {option.icon && <Icon svgType={option.icon} />}
        <button type="button" {...option.dataAttribute}>
          {option.label}
        </button>
      </Item>
    );
  }

  render() {
    const {
      buttonChildren,
      buttonClassName = '',
      buttonAriaLabel = '',
      listOnly,
      preferenceKey,
      items,
      readyList,
      side = 'bottom',
      sideOffset = 0,
      align = 'end',
      alignOffset = 0,
      avoidCollisions = true,
      label,
      contentClassName,
    } = this.props;

    const { visibleItems, hiddenItems, options, open } = this.state;

    if (!items) {
      return null;
    }

    const content = (
      <Content
        className={contentClassName || 'preferences-menu-content'}
        avoidCollisions={avoidCollisions}
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
      >
        <>
          {!listOnly && visibleItems.length > 0 && (
            <Label className="preferences-menu-label">
              {label ? label : `Edit ${preferenceKey}`}
            </Label>
          )}
          {visibleItems.length > 0 && this.renderVisibleItems()}
          {hiddenItems.length > 0 && this.renderHiddenItems()}
          {options.length > 0 && this.renderOptions()}
          {readyList?.length > 0 && this.renderList()}
        </>
      </Content>
    );

    return (
      <Root modal={false} open={open} onOpenChange={this.onClickToggleState}>
        <Trigger asChild>
          <button
            type="button"
            aria-label={
              buttonAriaLabel.length > 0
                ? buttonAriaLabel
                : `Modify ${preferenceKey}`
            }
            className={
              buttonClassName.length > 0
                ? buttonClassName
                : 'button button-xs button-secondary preferences-menu-trigger'
            }
          >
            {buttonChildren}
          </button>
        </Trigger>
        {listOnly ? content : <Portal>{content}</Portal>}
      </Root>
    );
  }
}
